import { useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ComicCard from '@/components/base/ComicCard';
import { getComicBySlug, getRelatedComics } from '@/mocks/comics';
import type { BreadcrumbItem } from '@/components/feature/Breadcrumb';

export default function ComicPage() {
  const { slug } = useParams<{ slug: string }>();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  const comic = getComicBySlug(slug || '');

  if (!comic) {
    return (
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="pt-16">
          <div className="w-full max-w-7xl mx-auto px-4 md:px-6 py-20 text-center">
            <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center rounded-full bg-background-100">
              <i className="ri-emotion-sad-line text-2xl text-foreground-300"></i>
            </div>
            <h1 className="text-xl font-heading text-foreground-800 mb-2">漫画未找到</h1>
            <p className="text-foreground-500 mb-6">抱歉，您要找的漫画不存在或已被移除。</p>
            <Link
              to="/"
              className="inline-flex px-6 py-3 bg-primary-500 text-background-50 rounded-full font-medium text-sm whitespace-nowrap cursor-pointer hover:bg-primary-600 transition-colors"
            >
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  const relatedComics = getRelatedComics(comic, 4);
  const viewsText = comic.views >= 10000 ? `${(comic.views / 10000).toFixed(1)}万` : comic.views.toString();

  const breadcrumbItems: BreadcrumbItem[] = [
    { name: '首页', path: '/' },
    { name: `${comic.category}漫画`, path: `/category/${comic.categorySlug}` },
    { name: comic.title },
  ];

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: `${comic.title} - 叮当漫画网无删减免费阅读`,
    description: comic.description,
    author: {
      '@type': 'Person',
      name: comic.author,
    },
    publisher: {
      '@type': 'Organization',
      name: '叮当漫画网',
    },
    datePublished: '2026-01-01',
    dateModified: comic.updatedAt,
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': `https://dingdangmh.com/comic/${comic.slug}`,
    },
  };

  return (
    <div className="min-h-screen bg-background-50">
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(articleSchema) }}
      />
      <Navbar />
      <main className="pt-16">
        <div className="bg-background-100 border-b border-background-200/70">
          <Breadcrumb items={breadcrumbItems} />
        </div>

        {/* Comic Info Section */}
        <section className="w-full max-w-7xl mx-auto px-4 md:px-6 py-8 md:py-10">
          <div className="flex flex-col md:flex-row gap-6 md:gap-8">
            {/* Cover */}
            <div className="flex-shrink-0 w-full sm:w-56 md:w-64 mx-auto md:mx-0">
              <div className="aspect-[3/4] rounded-lg overflow-hidden">
                <img
                  src={comic.cover}
                  alt={`${comic.title}漫画封面`}
                  title={`${comic.title} - 叮当漫画网免费阅读`}
                  className="w-full h-full object-cover object-top"
                />
              </div>
            </div>

            {/* Info */}
            <div className="flex-1">
              <h1 className="text-2xl md:text-3xl font-heading text-foreground-900 tracking-wide mb-3">
                {comic.title}
              </h1>

              <div className="flex items-center gap-3 flex-wrap mb-4">
                <span className="px-2.5 py-1 bg-secondary-100 text-secondary-700 text-xs font-medium rounded whitespace-nowrap">
                  {comic.category}
                </span>
                <span className={`px-2.5 py-1 text-xs font-medium rounded whitespace-nowrap ${
                  comic.status === '连载中'
                    ? 'bg-accent-100 text-accent-700'
                    : 'bg-background-200 text-foreground-600'
                }`}>
                  {comic.status}
                </span>
                {comic.tags.map((tag) => (
                  <span key={tag} className="px-2 py-0.5 bg-background-100 text-foreground-500 text-xs rounded whitespace-nowrap">
                    {tag}
                  </span>
                ))}
              </div>

              <div className="flex items-center gap-4 text-sm text-foreground-500 mb-5">
                <span className="flex items-center gap-1">
                  <span className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-user-line text-sm"></i>
                  </span>
                  {comic.author}
                </span>
                <span className="flex items-center gap-1">
                  <span className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-star-fill text-sm text-accent-500"></i>
                  </span>
                  <strong className="text-foreground-800">{comic.rating}</strong> 分
                </span>
                <span className="flex items-center gap-1">
                  <span className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-eye-line text-sm"></i>
                  </span>
                  {viewsText} 次阅读
                </span>
              </div>

              <p className="text-sm text-foreground-600 leading-relaxed mb-6">
                {comic.description}
              </p>

              <div className="flex flex-col sm:flex-row gap-3">
                <button className="flex items-center justify-center gap-2 px-8 py-3 bg-primary-500 hover:bg-primary-600 text-background-50 text-sm font-semibold rounded-full transition-colors whitespace-nowrap cursor-pointer">
                  <span className="w-5 h-5 flex items-center justify-center">
                    <i className="ri-play-circle-fill text-lg"></i>
                  </span>
                  开始阅读第一话
                </button>
                <button className="flex items-center justify-center gap-2 px-8 py-3 bg-background-100 hover:bg-background-200 text-foreground-700 text-sm font-medium rounded-full transition-colors whitespace-nowrap cursor-pointer border border-background-200/70">
                  <span className="w-5 h-5 flex items-center justify-center">
                    <i className="ri-bookmark-line text-lg"></i>
                  </span>
                  加入收藏
                </button>
              </div>
            </div>
          </div>
        </section>

        {/* Chapter List */}
        <section className="w-full bg-background-100 py-10 md:py-12" aria-labelledby="chapter-title">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="flex items-center justify-between mb-6">
              <h2 id="chapter-title" className="text-lg md:text-xl font-heading text-foreground-900 tracking-wide">
                章节列表
              </h2>
              <div className="flex items-center gap-2">
                <span className="text-xs text-foreground-400">共 {comic.chapters.length} 话</span>
                <button className="text-xs text-primary-500 hover:text-primary-600 font-medium whitespace-nowrap cursor-pointer">
                  倒序
                </button>
              </div>
            </div>

            <div className="bg-background-50 rounded-xl border border-background-200/70 overflow-hidden">
              {comic.chapters.map((chapter, index) => (
                <Link
                  key={chapter.id}
                  to={`/comic/${comic.slug}`}
                  className={`flex items-center justify-between px-5 py-4 hover:bg-background-100 transition-colors cursor-pointer ${
                    index < comic.chapters.length - 1 ? 'border-b border-background-200/70' : ''
                  }`}
                >
                  <div className="flex items-center gap-3">
                    <span className="text-sm font-semibold text-foreground-800 whitespace-nowrap">
                      {chapter.title}
                    </span>
                    {index === 0 && (
                      <span className="px-1.5 py-0.5 bg-accent-100 text-accent-700 text-[10px] font-medium rounded whitespace-nowrap">
                        NEW
                      </span>
                    )}
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="text-xs text-foreground-400 whitespace-nowrap">{chapter.updatedAt}</span>
                    <span className="w-5 h-5 flex items-center justify-center">
                      <i className="ri-arrow-right-s-line text-foreground-300"></i>
                    </span>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* Related Comics */}
        {relatedComics.length > 0 && (
          <section className="w-full max-w-7xl mx-auto px-4 md:px-6 py-12 md:py-16" aria-labelledby="related-title">
            <h2 id="related-title" className="text-lg md:text-xl font-heading text-foreground-900 tracking-wide mb-6">
              猜你喜欢
            </h2>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 md:gap-5">
              {relatedComics.map((item) => (
                <ComicCard key={item.id} comic={item} />
              ))}
            </div>
          </section>
        )}
      </main>
      <Footer />
    </div>
  );
}